// src/cockpit4/components/PrivateDomainAutomation.tsx
// ============================================
// 私域运营自动化（营销中心 P2-功能9）- v2.0 详细改进计划
// 社群自动运营 / 朋友圈内容规划 / 用户生命周期自动运营 / SOP 自动化执行
// 数据来源：localStorage（xeaos_private_domain）+ 内置自动化规则引擎
// ============================================

import { useMemo, useState } from "react";
import { motion } from "framer-motion";
import { MessageCircle, Users, Zap, Play, Pause, Mail, Sparkles } from "lucide-react";

interface LifecycleUser {
  id: string;
  name: string;
  stage: "新客" | "活跃" | "沉睡" | "流失" | "复购";
  lastActiveDays: number;
  orders: number;
  value: number;
}

const INITIAL_USERS: LifecycleUser[] = [
  { id: "u1", name: "王先生（酒店采购）", stage: "新客", lastActiveDays: 1, orders: 1, value: 8600 },
  { id: "u2", name: "李女士（连锁餐饮）", stage: "活跃", lastActiveDays: 2, orders: 5, value: 43200 },
  { id: "u3", name: "陈总（地产集团）", stage: "沉睡", lastActiveDays: 35, orders: 2, value: 15800 },
  { id: "u4", name: "赵经理（旅行社）", stage: "活跃", lastActiveDays: 4, orders: 8, value: 69000 },
  { id: "u5", name: "周老板（零售门店）", stage: "流失", lastActiveDays: 72, orders: 1, value: 4200 },
  { id: "u6", name: "吴总监（企业服务）", stage: "复购", lastActiveDays: 9, orders: 12, value: 156000 },
];

interface AutoTask {
  id: string;
  name: string;
  trigger: string;
  action: string;
  enabled: boolean;
}

const INITIAL_TASKS: AutoTask[] = [
  { id: "t1", name: "新客欢迎 SOP", trigger: "新用户进群", action: "自动发送欢迎语 + 新人礼包 + 产品手册", enabled: true },
  { id: "t2", name: "沉睡唤醒", trigger: "7 天未互动", action: "推送限时福利 + 定向优惠券", enabled: true },
  { id: "t3", name: "复购提醒", trigger: "上次购买 30 天", action: "推送回购提醒 + 老客专属价", enabled: true },
  { id: "t4", name: "流失挽回", trigger: "14 天未打开", action: "电话回访 + 专属挽回方案", enabled: false },
  { id: "t5", name: "社群促活", trigger: "每周五 19:00", action: "自动发起话题 + 社群活动 + 问答抽奖", enabled: true },
];

const KEY = "xeaos_private_domain";
const STAGES = ["新客", "活跃", "沉睡", "流失", "复购"] as const;

function load<T>(key: string, fallback: T): T {
  try {
    const raw = localStorage.getItem(key);
    if (raw) return JSON.parse(raw) as T;
  } catch { /* ignore */ }
  return fallback;
}

function nextAction(u: LifecycleUser): string {
  switch (u.stage) {
    case "新客": return "今日：发送欢迎+问卷";
    case "活跃": return "明日：推送新品";
    case "沉睡": return "今日：唤醒福利";
    case "流失": return "今日：电话回访";
    default: return "下月：会员日活动";
  }
}

export default function PrivateDomainAutomation() {
  const [users, setUsers] = useState<LifecycleUser[]>(() => load(KEY + "_users", INITIAL_USERS));
  const [tasks, setTasks] = useState<AutoTask[]>(() => load(KEY + "_tasks", INITIAL_TASKS));
  const [activeStage, setActiveStage] = useState<string>("全部");
  const [running, setRunning] = useState(false);

  const stageStats = useMemo(() => {
    const map = new Map<string, { count: number; value: number }>();
    for (const s of STAGES) map.set(s, { count: 0, value: 0 });
    for (const u of users) {
      const st = map.get(u.stage) ?? { count: 0, value: 0 };
      st.count += 1;
      st.value += u.value;
      map.set(u.stage, st);
    }
    return map;
  }, [users]);

  const filtered = users.filter((u) => activeStage === "全部" || u.stage === activeStage);
  const needWake = users.filter((u) => u.stage === "沉睡" || u.stage === "流失").length;

  const toggleTask = (id: string) => {
    setTasks((prev) => {
      const next = prev.map((t) => (t.id === id ? { ...t, enabled: !t.enabled } : t));
      try { localStorage.setItem(KEY + "_tasks", JSON.stringify(next)); } catch { /* ignore */ }
      return next;
    });
  };

  return (
    <div className="space-y-3">
      <div className="flex items-center gap-2">
        <MessageCircle size={14} className="text-emerald-400" />
        <h3 className="text-[12px] font-medium text-white">私域运营自动化</h3>
        <span className="text-[9px] font-mono text-gray-500 uppercase tracking-wider">P2-功能9 · Private Domain</span>
        <span className="text-[8px] font-mono text-amber-500/70 px-1.5 py-0.5 rounded bg-amber-500/10" title="预置示例客户便于演示，可编辑/新增真实客户，数据保存在本地浏览器；接 CRM 后可与客户中心联动">示例数据可编辑</span>
        <button
          onClick={() => setRunning((v) => !v)}
          className={`flex items-center gap-1 ml-auto px-2 py-1 rounded-md border text-[10px] transition-colors ${
            running ? "border-emerald-500/40 bg-emerald-500/15 text-emerald-300" : "border-white/[0.06] bg-white/[0.02] text-gray-400 hover:text-white"
          }`}
        >
          {running ? <Pause size={10} /> : <Play size={10} />}
          {running ? "自动化运行中" : "启动自动化"}
        </button>
      </div>

      <div className="grid grid-cols-6 gap-2">
        {STAGES.map((s) => {
          const st = stageStats.get(s) ?? { count: 0, value: 0 };
          const color = s === "新客" ? "#38BDF8" : s === "活跃" ? "#34D399" : s === "沉睡" ? "#FBBF24" : s === "流失" ? "#F87171" : "#A78BFA";
          return (
            <button
              key={s}
              onClick={() => setActiveStage(s)}
              className={`rounded-lg border p-2 text-left transition-colors ${activeStage === s ? "border-white/20 bg-white/[0.05]" : "border-white/[0.06] bg-white/[0.02] hover:bg-white/[0.04]"}`}
            >
              <div className="text-[9px] font-mono uppercase" style={{ color }}>{s}</div>
              <div className="text-sm font-semibold font-mono text-white mt-0.5">{st.count} 人</div>
              <div className="text-[9px] font-mono text-gray-500">¥{st.value.toLocaleString()}</div>
            </button>
          );
        })}
      </div>

      <div className="grid grid-cols-12 gap-3">
        <div className="col-span-7 rounded-lg border border-white/[0.06] bg-white/[0.02] p-3">
          <div className="flex items-center gap-2 mb-2">
            <Users size={11} className="text-emerald-400" />
            <span className="text-[10px] font-mono text-gray-400 uppercase tracking-wider">用户生命周期 · 自动运营 {activeStage !== "全部" ? `· ${activeStage}` : ""}</span>
            {needWake > 0 && <span className="ml-auto text-[9px] font-mono text-amber-400">⚠ {needWake} 人需唤醒</span>}
          </div>
          <div className="space-y-1.5">
            {filtered.map((u) => (
              <div key={u.id} className="flex items-center gap-2 rounded-md border border-white/[0.04] bg-white/[0.01] px-2.5 py-1.5">
                <span className="text-[10px] text-white">{u.name}</span>
                <span className={`text-[9px] font-mono px-1.5 py-0.5 rounded ${
                  u.stage === "新客" ? "bg-sky-500/15 text-sky-300" : u.stage === "活跃" ? "bg-emerald-500/15 text-emerald-300" : u.stage === "沉睡" ? "bg-amber-500/15 text-amber-300" : u.stage === "流失" ? "bg-red-500/15 text-red-300" : "bg-purple-500/15 text-purple-300"
                }`}>{u.stage}</span>
                <span className="text-[9px] font-mono text-gray-600">{u.lastActiveDays}天前活跃</span>
                <span className="text-[9px] font-mono text-gray-600">下单{u.orders}次</span>
                <span className="ml-auto text-[9px] font-mono text-emerald-400">¥{u.value.toLocaleString()}</span>
                <span className="text-[9px] font-mono text-cyan-400">{nextAction(u)}</span>
              </div>
            ))}
            {filtered.length === 0 && <div className="text-[10px] text-gray-500 py-4 text-center">暂无该阶段用户</div>}
          </div>
        </div>

        <div className="col-span-5 rounded-lg border border-white/[0.06] bg-white/[0.02] p-3">
          <div className="flex items-center gap-2 mb-2">
            <Zap size={11} className="text-amber-400" />
            <span className="text-[10px] font-mono text-gray-400 uppercase tracking-wider">SOP 自动化任务</span>
          </div>
          <div className="space-y-1.5">
            {tasks.map((t) => (
              <div key={t.id} className={`flex items-start gap-2 rounded-md border p-2 ${t.enabled ? "border-emerald-500/20 bg-emerald-500/[0.03]" : "border-white/[0.04] bg-white/[0.01] opacity-60"}`}>
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-1.5">
                    <span className="text-[10px] font-medium text-white">{t.name}</span>
                    <span className={`text-[9px] font-mono px-1 rounded ${t.enabled ? "bg-emerald-500/15 text-emerald-400" : "bg-gray-500/15 text-gray-500"}`}>{t.enabled ? "启用" : "停用"}</span>
                  </div>
                  <div className="text-[9px] font-mono text-gray-500 mt-0.5">触发：{t.trigger}</div>
                  <div className="text-[9px] font-mono text-gray-600">{t.action}</div>
                </div>
                <button onClick={() => toggleTask(t.id)} className="text-[9px] font-mono text-gray-400 hover:text-white mt-0.5">{t.enabled ? "停用" : "启用"}</button>
              </div>
            ))}
          </div>
        </div>
      </div>

      <div className="rounded-lg border border-white/[0.06] bg-white/[0.02] p-3">
        <div className="flex items-center gap-2 mb-2">
          <Mail size={11} className="text-sky-400" />
          <span className="text-[10px] font-mono text-gray-400 uppercase tracking-wider">朋友圈内容规划 · 本周</span>
        </div>
        <div className="grid grid-cols-7 gap-1.5">
          {["周一", "周二", "周三", "周四", "周五", "周六", "周日"].map((d, i) => (
            <div key={d} className="rounded-md border border-white/[0.04] bg-white/[0.01] p-2">
              <div className="text-[9px] font-mono text-gray-500">{d}</div>
              <div className="text-[9px] text-gray-300 mt-1 leading-relaxed">
                {i === 0 ? "产品干货 + 客户案例" : i === 1 ? "行业洞察 + 数据图表" : i === 2 ? "客户好评截图" : i === 3 ? "团队日常 + 价值观" : i === 4 ? "促销预告 + 倒计时" : i === 5 ? "周末福利 + 互动话题" : "复盘 + 下周预告"}
              </div>
              <div className="text-[8px] font-mono text-emerald-400/70 mt-1">AI 自动生成</div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

